import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { useMessagesWithExptAndChoreoDisabled } from '@atlassian/post-office-context/narrow-exposure/use-messages-with-expt-and-choreo-disabled';
import { useMessagePlacementHooks } from '@atlassian/post-office-context/message-placement';

import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';

import { MaybeSuspenseBoundary } from '@post-office/placement-common/utils/message-renderer';

import { useInitialData } from '@post-office/placement-contexts/hooks';

import { memo, type ReactNode, useCallback, useEffect, useState } from 'react';

import { MessageRenderer } from '../__generated__/client/render';

import { PLACEMENT_ID } from '../__generated__/common';

import { type JiraSideNavInitialData } from '../api/types';

import { type Placement as JiraSideNavPlacement } from '../contracts/Placement';

import { getMessage, messageExperimentConfigs, type Message } from '../utils/get-message';

import { type ErrorBoundariedProps } from '@post-office/placement-contexts/create-placement-component/types';

type MessageState = {
	message?: Message;
	messages?: JiraSideNavInitialData['messages'];
	selectionComplete: boolean;
	status: 'loading' | 'done';
};

const selectResponse = (
	messages: JiraSideNavInitialData['messages'],
	isMigrationEnabled: boolean,
): MessageState => {
	const legacyMessage: JiraSideNavInitialData['messages'][number] | undefined = isMigrationEnabled
		? undefined
		: getMessage(
				messages.filter(
					(message) =>
						// do not include narrow exposure messages that do not have legacy implementation if migration not enabled
						!message.inAppNarrowExposureExperiment ||
						messageExperimentConfigs[message.messageTemplateId],
				),
			);
	return {
		messages,
		// Legacy selection already evaluated the experiment; do not let the renderer gate hide it.
		message: legacyMessage
			? { ...legacyMessage, inAppNarrowExposureExperiment: undefined }
			: undefined,
		selectionComplete: !isMigrationEnabled,
		status: 'done',
	};
};

export const JiraSideNavPlacementComponent: (
	props: ErrorBoundariedProps<JiraSideNavPlacement>,
) => ReactNode = createPlacementComponent<JiraSideNavPlacement>(
	PLACEMENT_ID,
	memo(({ initialData, preloadedInitialData, handleClearPreloadedData }) => {
		const isMigrationEnabled =
			FeatureGates.checkGate('post-office_enable_no-choreo-expt-migration') &&
			FeatureGates.checkGate('post-office_enable_narrow-exposure-classic');
		const { data, isLoading } = useInitialData<JiraSideNavInitialData>(initialData);
		const [{ message: savedMessage, messages, selectionComplete, status }, setMessageState] =
			useState<MessageState>(() =>
				preloadedInitialData
					? selectResponse(preloadedInitialData.messages ?? [], isMigrationEnabled)
					: { selectionComplete: false, status: 'loading' },
			);

		const handleOnDismissed = useCallback(() => {
			setMessageState({ selectionComplete: true, status: 'done' });

			if (typeof handleClearPreloadedData === 'function') {
				handleClearPreloadedData();
			}
		}, [handleClearPreloadedData]);

		const { onMessageDismiss, onMessageClicked, onMessageSnooze } = useMessagePlacementHooks();

		useEffect(() => onMessageDismiss(handleOnDismissed), [onMessageDismiss, handleOnDismissed]);
		useEffect(() => onMessageClicked(handleOnDismissed), [onMessageClicked, handleOnDismissed]);
		useEffect(() => onMessageSnooze(handleOnDismissed), [onMessageSnooze, handleOnDismissed]);

		const isDone = status === 'done';

		useEffect(() => {
			if (!isLoading && !isDone) {
				setMessageState(selectResponse(data?.messages ?? [], isMigrationEnabled));
			}
		}, [data, isLoading, isDone, isMigrationEnabled]);

		const [selectedMessage] = useMessagesWithExptAndChoreoDisabled(
			!selectionComplete && status === 'done'
				? (messages ?? []).filter(
						(candidate) =>
							!['test-message-inapp', 'message-template-template'].includes(
								candidate.messageTemplateId,
							),
					)
				: [],
			{ maxMessagesToDisplay: 1 },
		);
		const message = selectionComplete ? savedMessage : selectedMessage;

		useEffect(() => {
			if (status === 'done' && !selectionComplete) {
				// Like the legacy selector, keep this response's decision until it is dismissed.
				setMessageState((previous) =>
					previous.selectionComplete
						? previous
						: { message, selectionComplete: true, status: 'done' },
				);
			}
		}, [message, selectionComplete, status]);

		if (status === 'loading' || !message) {
			return null;
		}

		return (
			<MessageRenderer
				{...message}
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
				id={message.messageInstanceId}
				onDismissed={handleOnDismissed}
				SuspenseBoundary={MaybeSuspenseBoundary}
			/>
		);
	}),
	{
		choreographer: {
			isDefaultEnabled: false,
		},
	},
);

export default JiraSideNavPlacementComponent;
